import React, { useCallback, useRef, useState } from 'react';
import styled from 'styled-components';
import Icon from '@mdi/react';
import { mdiCloudUploadOutline, mdiFileOutline, mdiClose } from '@mdi/js';
import { adminTheme } from '../styles/adminTheme';

const Zone = styled.div<{ $dragging: boolean }>`
  border: 2px dashed ${p => p.$dragging ? adminTheme.colors.primary : adminTheme.colors.border};
  border-radius: ${adminTheme.borderRadius};
  padding: 24px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  background: ${p => p.$dragging ? '#e8f0fe' : 'transparent'};

  &:hover {
    border-color: ${adminTheme.colors.primary};
    background: #f8f9ff;
  }
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${adminTheme.colors.textMuted};
  margin-top: 8px;
`;

const SelectedFile = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #f8f9fa;
  border-radius: 4px;
  font-size: 13px;
  color: ${adminTheme.colors.text};
  margin-top: 8px;
`;

const RemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  margin-left: auto;
  color: ${adminTheme.colors.textMuted};

  &:hover {
    color: ${adminTheme.colors.danger};
  }
`;

const UploadButton = styled.button`
  margin-top: 12px;
  padding: 8px 20px;
  background: ${adminTheme.colors.primary};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: ${adminTheme.colors.primaryHover};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ResultMsg = styled.div<{ $error?: boolean }>`
  margin-top: 8px;
  font-size: 12px;
  color: ${p => p.$error ? adminTheme.colors.danger : adminTheme.colors.success};
`;

interface FileUploadZoneProps {
  accept?: string;
  onUpload: (file: File) => Promise<{ success: boolean; message: string }>;
}

export const FileUploadZone: React.FC<FileUploadZoneProps> = ({
  accept = '.xlsx,.csv',
  onUpload,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) { setFile(f); setResult(null); }
  }, []);

  const handleSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) { setFile(f); setResult(null); }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await onUpload(file);
      setResult(res);
      if (res.success) setFile(null);
    } catch (e: any) {
      setResult({ success: false, message: e.message || 'Upload failed' });
    }
    setUploading(false);
  }, [file, onUpload]);

  return (
    <div>
      <Zone
        $dragging={dragging}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <Icon path={mdiCloudUploadOutline} size={1.2} color={adminTheme.colors.textMuted} />
        <Hint>Drop file here or click to browse</Hint>
        <Hint>Accepted: {accept}</Hint>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleSelect}
          style={{ display: 'none' }}
        />
      </Zone>

      {file && (
        <>
          <SelectedFile>
            <Icon path={mdiFileOutline} size={0.65} color={adminTheme.colors.textSecondary} />
            {file.name} ({(file.size / 1024).toFixed(1)} KB)
            <RemoveBtn onClick={() => { setFile(null); setResult(null); }}>
              <Icon path={mdiClose} size={0.55} />
            </RemoveBtn>
          </SelectedFile>
          <UploadButton onClick={handleUpload} disabled={uploading}>
            {uploading ? 'Uploading...' : 'Upload'}
          </UploadButton>
        </>
      )}

      {result && (
        <ResultMsg $error={!result.success}>{result.message}</ResultMsg>
      )}
    </div>
  );
};
